import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { FormLayout, Form, Button, Banner } from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchById } from "services/orders.service";
import _ from "lodash";
import "./style.scss";
import { AppDispatch } from "configureStore";
import ProductsSelect from "components/products-select/ProductsSelect";
import ProductGroupSelect from "components/products-select/ProductGroupSelect";
import { Item } from "types/Order";
import { ProductGroupItem } from "types/Product";
import { post, patch } from "services/orders.service";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import Loading from "components/loading/Loading";
import { fetchOrders } from "features/orders/orders.slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";

type OrderItem = {
  id?: string;
  [key: string]: any;
};

function OrderProductsForm({ id }: { id?: string }) {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { companyId: company } = useParams<{ companyId: string }>();

  const [orderItems, setOrderItems] = useState<any>([]);
  const [productGroups, setProductGroups] = useState<ProductGroupItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [initialOrderitems, setInitialOrderitems] = useState<any>([]);
  const [initialProductGroups, setInitialProductGroups] = useState<ProductGroupItem[]>([]);
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const validateForm = (): boolean => {
    let isValid = true;

    setErrMsg("");

    // Product or Product Group check
    if (orderItems.length === 0 && productGroups.length === 0) {
      setErrMsg(t("at_least_one_product_or_product_group_required"));
      isValid = false;
    }

    return isValid;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    const productsTotal = _.map(
      orderItems,
      (item) => (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0)
    ).reduce((a, b) => a + b, 0);

    const groupsTotal = _.map(
      productGroups,
      (group) => (Number(group.quantity) || 0) * (Number(group.price) || 0)
    ).reduce((a, b) => a + b, 0);

    const totalPrice = productsTotal + groupsTotal;

    const formData = {
      id,
      orderItems,
      productGroups,
      company,
      totalPrice,
    };
    const action = id ? patch : post;
    try {
      setSaveBtnLoading(true);
      await action(formData);
      if (company)
        dispatch(fetchOrders({ filterValue: {}, companyId: company }));
      if (company && id)
        dispatch(fetchOrderDetails({ companyId: company, orderId: id }));

      setSuccessMessage("ok");
      setSaveBtnLoading(false);
      setTimeout(() => {
        closeModal();
      }, 1000);
    } catch (e) {
      console.error(e);
      setErrMsg("error");
      setSaveBtnLoading(false);
    }
  };

  function transformOrderItem(dbItem: Item): OrderItem {
    return {
      id: dbItem.id,
      title: dbItem.productTitle,
      price: dbItem.unitPrice ?? "",
      quantity: dbItem.quantity ?? "",
      product: dbItem.product,
      notes: dbItem.description ?? undefined,
      sku: dbItem.sku ?? "",
    };
  }

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const { orderEntity } = await fetchById(id!, company!);
        const transformed = orderEntity.items.map(transformOrderItem);
        setOrderItems(transformed);
        setInitialOrderitems(transformed);
        
        // Load product groups with quantity and price
        const productGroupItems = (orderEntity.productGroups || []).map((pg: any) => ({
          id: pg.id,
          productGroup: pg.productGroup?.id || pg.productGroup,
          quantity: pg.quantity || 1,
          price: pg.unitPrice || 0,
          notes: pg.description || "",
        }));
        setProductGroups(productGroupItems);
        setInitialProductGroups(productGroupItems);
        
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        console.error(e);
      }
    }

    if (id) {
      fetchData();
    }
  }, [id, dispatch]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div>
      <div>
        <Form onSubmit={handleFormSubmit}>
          <FormLayout>
            <div className="form-warnings">
              {errMsg && <Banner tone="critical">{t(errMsg)}</Banner>}
              {successMessage && (
                <Banner tone="info">{t(successMessage)}</Banner>
              )}
            </div>
            <ProductsSelect
              onChange={(value: any) => {
                if (JSON.stringify(orderItems) !== JSON.stringify(value)) {
                  setOrderItems(value);
                }
              }}
              initialProducts={initialOrderitems}
              isRequired={false}
            />
            <ProductGroupSelect
              onProductGroupsChange={(productGroupItems: ProductGroupItem[]) => {
                setProductGroups(productGroupItems);
              }}
              initialProductGroups={initialProductGroups}
              isRequired={false}
            />
            <div>
              <Button submit loading={saveBtnLoading}>
                {t("save")}
              </Button>
            </div>
          </FormLayout>
        </Form>
      </div>
    </div>
  );
}

export default OrderProductsForm;
